import { useMemo, useState } from "react";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import Cache from "../utils/Cache";
import { fuzzyMatchScore } from "../utils/helperFunctions";

const HISTORY_LIMIT = 8;
const SUGGESTION_LIMIT = 8;
const MIN_HISTORY_LENGTH = 2;

const readHistory = (storageKey) => {
  try {
    const parsed = JSON.parse(Cache.get(storageKey));
    return Array.isArray(parsed)
      ? parsed.filter((term) => typeof term === "string")
      : [];
  } catch (error) {
    return [];
  }
};

const writeHistory = (storageKey, history) => {
  try {
    Cache.set(storageKey, JSON.stringify(history));
  } catch (error) {}
};

// Free text search with fuzzy suggestions, backed by a locally persisted list
// of previously used terms. Recent terms are listed first and are removable.
const SearchWithHistory = ({
  value,
  onChange,
  storageKey,
  suggestions = [],
  suggestionsGroupLabel = "Results",
  placeholder = "Search by name...",
  sx,
}) => {
  const [history, setHistory] = useState(() => readHistory(storageKey));

  const options = useMemo(() => {
    const recentTerms = history.map((term) => term.toLowerCase());
    return [
      ...history.map((term) => ({ label: term, isRecent: true })),
      ...suggestions
        .filter((term) => !recentTerms.includes(String(term).toLowerCase()))
        .map((term) => ({ label: term, isRecent: false })),
    ];
  }, [history, suggestions]);

  const filterOptions = (allOptions, { inputValue }) =>
    allOptions
      .map((option) => ({
        option,
        score: fuzzyMatchScore(option.label, inputValue),
      }))
      .filter(({ score }) => score !== null)
      .sort(
        (a, b) => b.option.isRecent - a.option.isRecent || a.score - b.score
      )
      .slice(0, SUGGESTION_LIMIT)
      .map(({ option }) => option);

  const commitToHistory = (text) => {
    const term = (text || "").trim();
    if (term.length < MIN_HISTORY_LENGTH) return;
    if (
      !suggestions.some(
        (suggestion) => fuzzyMatchScore(suggestion, term) !== null
      )
    )
      return;

    const next = [
      term,
      ...history.filter((item) => item.toLowerCase() !== term.toLowerCase()),
    ].slice(0, HISTORY_LIMIT);
    setHistory(next);
    writeHistory(storageKey, next);
  };

  const removeFromHistory = (term) => {
    const next = history.filter((item) => item !== term);
    setHistory(next);
    writeHistory(storageKey, next);
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      selectOnFocus
      options={options}
      filterOptions={filterOptions}
      groupBy={(option) =>
        option?.isRecent ? "Recent" : suggestionsGroupLabel
      }
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.label || ""
      }
      inputValue={value}
      onInputChange={(event, inputValue) => onChange(inputValue)}
      onChange={(event, selected) => {
        const label =
          typeof selected === "string" ? selected : selected?.label || "";
        onChange(label);
        commitToHistory(label);
      }}
      onBlur={() => commitToHistory(value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") commitToHistory(value);
      }}
      renderOption={(props, option) => (
        <li {...props} key={`${option.isRecent}-${option.label}`}>
          <span
            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {option.label}
          </span>
          {option.isRecent ? (
            <Tooltip title="Remove from recent searches">
              <IconButton
                size="small"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  removeFromHistory(option.label);
                }}
              >
                <Close fontSize="inherit" />
              </IconButton>
            </Tooltip>
          ) : null}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      sx={sx}
    />
  );
};

export default SearchWithHistory;
